/**
 * CustomerActivityTimelinePremium - Timeline premium de atividades do cliente
 * Design espelhado da DealActivityTimelinePremium (paridade visual/funcional)
 * Exibe atividades vinculadas diretamente ao cliente ou via deals
 *
 * @version 7.5.0
 * RELEASE: 2026-10-09 v7.5.0 - BR-OS-028: eventos da Ordem de Serviço (new_value.source =
 *   'service_order') com ícone Wrench laranja e linha de detalhe com o número da OS.
 * RELEASE: 2026-09-03 v7.4.0 - Evento de perda (status_change -> lost) ganha linha
 *   secundária com motivo + observação, espelhando a v6.0.0 da timeline de negócios.
 * RELEASE: 2026-08-09 v7.0.0 - Refino visual: ícone sem círculo cinza (cor por tipo/módulo),
 *   referência do negócio como linha discreta (sem caixa/borda) e card mais clean.
 * RELEASE: 2026-08-09 v6.0.0 - Layout dos cards padronizado com a timeline do modal de
 *   tarefas: ícone fora do card, cabeçalho compacto (título text-xs, badges h-4,
 *   hora/avatar/ações à direita) e corpo px-3 com blocos de conteúdo.
 * RELEASE: 2026-07-11 v5.0.0 - Paridade visual com DealActivityTimelinePremium:
 *   remoção da linha vertical conectora, remoção da borda dos cards e recuo
 *   ml-12/-left-12 para dar mais respiro ao ícone.
 * RELEASE: 2026-07-11 v4.0.0 - Paridade funcional (realtime, edição, badges).
 * RELEASE: 2026-01-10 v3.0.0 - Redesign com agrupamento por data e avatares
 */
import React, { useRef, useEffect, useCallback } from 'react';
import { format, isPast, isFuture } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  FileText,
  Phone,
  Mail,
  Calendar,
  CheckSquare,
  ArrowRightLeft,
  User,
  TrendingUp,
  TrendingDown,
  RefreshCw,
  Trash2,
  CheckCircle2,
  Clock,
  CalendarClock,
  AlertCircle,
  Plus,
  Briefcase,
  RotateCcw,
  Loader2,
  FileSignature,
  CreditCard,
  Pencil,
  XCircle,
  DollarSign,
  Hash,
  Tag,
  Flag,
  Link2,
  Unlink,
  BookOpen,
  ScrollText,
  Wrench,
} from 'lucide-react';
// v7.5.0: detalhe dos eventos da OS (mesmo texto da timeline do Veículo)
import { describeServiceOrderEvent } from '@/components/vehicles/modal/vehicleAuditEvents';
// v7.2.0: metadados visuais dos eventos de proposta (compartilhados com o modal de negócios)
import { resolveProposalEventMeta } from '@/components/crm/shared/proposalActivityMeta';
// v7.3.0: card unificado de cobranças (avulsa/agrupada por assinatura)
import { ChargeActivityCard } from '@/components/crm/shared/ChargeActivityCard';
import { useCustomerActivities, CustomerActivity } from '@/hooks/useCustomerActivities';
import { useCustomerActivitiesPaginated } from '@/hooks/useCustomerActivitiesPaginated';
import { useCustomerActivitiesRealtime } from '@/hooks/useCustomerActivitiesRealtime';
import { ActivityType } from '@/hooks/useCrmDealActivities';
import { ActivityAttachmentsDisplay } from '@/components/crm/deals/ActivityAttachmentsDisplay';
import { ActivityEditSheet } from '@/components/crm/deals/modal/components/ActivityEditSheet';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '@/components/ui/tooltip';
import { SmartAvatar } from '@/components/ui/smart-avatar';
import { cn } from '@/lib/utils';
import googleMeetIcon from '@/assets/google-meet-icon.svg';
import { formatDealCode } from '@/lib/formatters';
import { groupByDatePreserveOrder, DateGroup } from '@/lib/utils/group-by-date';
import { ActivityCommentSection } from '@/components/crm/activities/ActivityCommentSection';

// v4.0.0: eventos meta do Playbook (logados como 'note' + payload em new_value.source='playbook')
const PLAYBOOK_META_EVENTS = new Set<string>([
  'playbook_started',
  'playbook_cancelled',
  'playbook_completed',
  'playbook_reopened',
  'playbook_closed_by_status',
  'playbook_synced',
  'playbook_phase_added',
  'playbook_phase_skipped',
]);

// v4.0.0: labels traduzidos de prioridade de tarefa (system activity 'task')
const TASK_PRIORITY_LABELS: Record<string, string> = {
  low: 'Baixa',
  normal: 'Normal',
  medium: 'Média',
  high: 'Alta',
  urgent: 'Urgente',
};

// Props do componente
interface CustomerActivityTimelinePremiumProps {
  /** ID do cliente (null no modo Contato) */
  customerId: string | null;
  /** v7.2.0: ID do contato — timeline compartilhada contato <-> cliente */
  contactId?: string | null;
  maxItems?: number;
  isActive?: boolean; // Para lazy loading
  /** v4.0.0: Ativa paginação server-side com scroll infinito */
  paginated?: boolean;
  /** v4.0.0: Modo compacto (esconde comentários e ações) — paridade com deal */
  compact?: boolean;
}

// Mapeamento de ícones por tipo de atividade
const activityIcons: Record<ActivityType, React.ComponentType<{ className?: string }>> = {
  note: FileText,
  call: Phone,
  email: Mail,
  meeting: Calendar,
  task: CheckSquare,
  phase_change: ArrowRightLeft,
  status_change: RefreshCw,
  assignment: User,
  value_change: TrendingUp,
  created: Plus,
  product_added: TrendingUp,
  product_removed: TrendingDown,
  contract_generated: FileSignature,
  charge_generated: CreditCard,
};

// v7.0.0: cor do ícone por tipo/módulo (sem círculo de fundo)
const activityIconColors: Record<ActivityType, string> = {
  note: 'text-muted-foreground',
  call: 'text-emerald-600 dark:text-emerald-400',
  email: 'text-violet-600 dark:text-violet-400',
  meeting: 'text-orange-600 dark:text-orange-400',
  task: 'text-amber-600 dark:text-amber-400',
  phase_change: 'text-slate-500',
  status_change: 'text-slate-500',
  assignment: 'text-indigo-600 dark:text-indigo-400',
  value_change: 'text-teal-600 dark:text-teal-400',
  created: 'text-slate-500',
  product_added: 'text-teal-600 dark:text-teal-400',
  product_removed: 'text-rose-600 dark:text-rose-400',
  contract_generated: 'text-indigo-600 dark:text-indigo-400',
  charge_generated: 'text-teal-600 dark:text-teal-400',
};

/**
 * Componente separador de data
 * v7.0.0: pl-9 acompanha a nova largura da coluna do ícone
 */
function DateSeparator({ label }: { label: string }) {
  return (
    <div className="flex items-center justify-center my-4 pl-9">

      <div className="flex-1 h-px bg-border" />
      <span className="px-3 py-0.5 text-xs font-medium text-muted-foreground bg-muted rounded-full">
        {label}
      </span>
      <div className="flex-1 h-px bg-border" />
    </div>
  );
}

/**
 * Timeline premium de atividades do cliente
 */
export function CustomerActivityTimelinePremium({ 
  customerId, 
  contactId = null,
  maxItems,
  isActive,
  paginated = false,
  compact = false
}: CustomerActivityTimelinePremiumProps) {
  // v4.0.0 - Realtime: invalida cache quando qualquer atividade do cliente muda
  useCustomerActivitiesRealtime(customerId);

  // v4.0.0 - Estado da atividade em edição (abre ActivityEditSheet)
  const [editingActivity, setEditingActivity] = React.useState<CustomerActivity | null>(null);

  // Estado para expandir/colapsar descrições longas
  const [expandedDescriptions, setExpandedDescriptions] = React.useState<Set<string>>(new Set());

  /** Alterna expansão da descrição de uma atividade */
  const toggleDescription = (id: string) => {
    setExpandedDescriptions(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  // Hook paginado (server-side) - sempre chamado mas desabilitado quando não paginado
  const paginatedResult = useCustomerActivitiesPaginated(customerId, {
    enabled: paginated && (isActive ?? true),
    contactId, // v7.2.0: modo Contato / timeline compartilhada
  });

  // Hook legado (client-side) - sempre chamado mas desabilitado quando paginado
  const legacyResult = useCustomerActivities(
    customerId as string,
    { enabled: !paginated && (isActive ?? true), limit: maxItems ? maxItems + 10 : 50 }
  );

  // Seleciona resultado com base no modo
  const {
    activities: paginatedActivities,
    isLoading: paginatedLoading,
    completeActivity: paginatedComplete,
    reopenActivity: paginatedReopen,
    updateActivity: paginatedUpdate,
    deleteActivity: paginatedDelete,
  } = paginatedResult;

  const {
    data: legacyActivities,
    isLoading: legacyLoading,
    completeActivity: legacyComplete,
    reopenActivity: legacyReopen,
    updateActivity: legacyUpdate,
    deleteActivity: legacyDelete,
  } = legacyResult;

  // Unifica interface entre os dois modos
  const rawActivities = paginated ? (paginatedActivities || []) : (legacyActivities || []);
  const isLoading = paginated ? paginatedLoading : legacyLoading;
  const completeActivity = paginated ? paginatedComplete : legacyComplete;
  const reopenActivity = paginated ? paginatedReopen : legacyReopen;
  const updateActivity = paginated ? paginatedUpdate : legacyUpdate;
  const deleteActivity = paginated ? paginatedDelete : legacyDelete;

  // Limita items se necessário (apenas no modo legado)
  const displayedActivities = (!paginated && maxItems) ? rawActivities.slice(0, maxItems) : rawActivities;
  const hasMore = (!paginated && maxItems) ? rawActivities.length > maxItems : false;

  // Agrupa atividades por data
  const groupedActivities = groupByDatePreserveOrder(displayedActivities);

  // Ref do sentinela para IntersectionObserver (scroll infinito)
  const sentinelRef = useRef<HTMLDivElement>(null);

  /**
   * IntersectionObserver para disparar fetchNextPage no modo paginado
   */
  const handleIntersection = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const [entry] = entries;
      if (entry.isIntersecting && paginated && paginatedResult.hasNextPage && !paginatedResult.isFetchingNextPage) {
        paginatedResult.fetchNextPage();
      }
    },
    [paginated, paginatedResult.hasNextPage, paginatedResult.isFetchingNextPage, paginatedResult.fetchNextPage]
  );

  useEffect(() => {
    if (!paginated) return;
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(handleIntersection, {
      rootMargin: '200px',
      threshold: 0,
    });

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [paginated, handleIntersection]);

  // Verifica se atividade tem agendamento pendente
  // v3.1.0 — Notas não têm ciclo de conclusão
  const hasSchedulePending = (activity: CustomerActivity) => {
    if (activity.activity_type === 'note') return false;
    return activity.scheduled_at && !activity.completed_at;
  };

  // Verifica se agendamento está atrasado
  const isOverdue = (activity: CustomerActivity) => {
    if (activity.activity_type === 'note') return false;
    if (!activity.scheduled_at || activity.completed_at) return false;
    return isPast(new Date(activity.scheduled_at));
  };

  // Verifica se agendamento é futuro
  const isUpcoming = (activity: CustomerActivity) => {
    if (activity.activity_type === 'note') return false;
    if (!activity.scheduled_at || activity.completed_at) return false;
    return isFuture(new Date(activity.scheduled_at));
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3].map(i => (
          <div key={i} className="flex gap-3 p-4 rounded-xl border bg-card shadow-sm">
            <Skeleton className="h-8 w-8 rounded-full flex-shrink-0" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-3 w-1/2" />
            </div>
            <Skeleton className="h-7 w-7 rounded-full" />
          </div>
        ))}
      </div>
    );
  }

  // Estado vazio
  if (rawActivities.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-10 text-center">
        <div className="h-12 w-12 rounded-full bg-muted/50 flex items-center justify-center mb-3">
          <Clock className="h-6 w-6 text-muted-foreground" />
        </div>
        <p className="text-sm font-medium text-muted-foreground">
          Nenhuma atividade registrada
        </p>
        <p className="text-xs text-muted-foreground/70 mt-1">
          Adicione notas, ligações ou reuniões
        </p>
      </div>
    );
  }

  return (
    <TooltipProvider>
      {/* Container principal (v5.0.0: sem linha vertical, paridade com Deal Timeline) */}
      <div>
        <div className="space-y-1">
          {groupedActivities.map((group: DateGroup<CustomerActivity>, groupIndex: number) => (
            <div key={group.dateKey}>
              {/* Separador de data */}
              <DateSeparator label={group.dateLabel} />
              
              {/* Cards de atividades do grupo */}
              <div className="space-y-2">
                {group.items.map((activity) => {
                  const activityType = activity.activity_type as ActivityType;
                  // v7.2.0: eventos de PROPOSTA (origem 'proposal') usam ícone/cor
                  // semânticos compartilhados com a timeline do modal de negócios
                  const proposalNv = activity.is_system
                    ? ((activity as any).new_value as Record<string, any> | null)
                    : null;
                  const proposalMeta = resolveProposalEventMeta(proposalNv);
                  const isProposal = !!proposalMeta;

                  // v7.4.0: detecta evento de PERDA (status_change para 'lost') para
                  // exibir motivo + observação em linha secundária (paridade com negócios).
                  const statusNv = activityType === 'status_change'
                    ? ((activity as any).new_value as Record<string, any> | null)
                    : null;
                  const isLostEvent = statusNv?.status === 'lost';
                  const lossReasonName: string | null = statusNv?.loss_reason_name ?? null;
                  const lossReasonNote: string | null = statusNv?.loss_reason_note ?? null;
                  const lossReasonColor: string | null = statusNv?.loss_reason_color ?? null;
                  const hasLossDetail = isLostEvent && (!!lossReasonName || !!lossReasonNote);
                  // v7.5.0: evento replicado da Ordem de Serviço
                  const isServiceOrder = proposalNv?.source === 'service_order';
                  const serviceOrderDetail = isServiceOrder ? describeServiceOrderEvent(proposalNv) : null;
                  const Icon = isServiceOrder
                    ? Wrench
                    : proposalMeta?.icon || activityIcons[activityType] || FileText;
                  // v7.0.0: cor por tipo (eventos de sistema não são mais forçados a cinza)
                  const iconColorClass = isServiceOrder
                    ? 'text-orange-600 dark:text-orange-400'
                    : proposalMeta?.color || activityIconColors[activityType] || activityIconColors.note;
                  const isPending = hasSchedulePending(activity);
                  const overdue = isOverdue(activity);
                  const upcoming = isUpcoming(activity);
                  
                  return (
                    // v7.0.0: ícone sem círculo + card clean
                    <div key={activity.id} className="flex gap-2.5 group">
                      {/* Ícone do tipo de atividade (coluna esquerda, sem fundo) */}
                      <div className="h-8 w-6 flex items-center justify-center flex-shrink-0">
                        <Icon className={cn('h-4 w-4', iconColorClass)} />
                      </div>

                      {/* Card do andamento
                          v7.1.0: restaurados os efeitos premium do card (sombra base,
                          elevação no hover e leve destaque de borda), iguais aos cards
                          da timeline do modal de tarefas. Os ícones semânticos da
                          v7.0.0 permanecem inalterados. */}
                      <div
                        className={cn(
                          'flex-1 min-w-0 rounded-lg border border-border/60 bg-card',
                          'shadow-sm hover:shadow-md hover:border-border',
                          'transition-all duration-200',
                          // v7.2.0: barra lateral semântica dos eventos de proposta
                          isProposal && cn('border-l-3', proposalMeta!.accent),
                          activity.activity_type !== 'note' && activity.completed_at && 'opacity-75',
                        )}
                      >



                        {/* Cabeçalho: título + badges à esquerda, hora/avatar/ações à direita */}
                        <div className="flex items-start justify-between gap-2 px-3 py-2">
                          <div className="flex items-center gap-1.5 flex-wrap min-w-0">
                            <span
                              className={cn(
                                'text-[13px] font-semibold text-foreground',
                                activity.activity_type !== 'note' &&
                                  activity.completed_at &&
                                  'line-through text-muted-foreground',
                              )}
                            >
                              {activity.title}
                            </span>

                            {activity.performed_by_user && (
                              <span className="text-[11px] text-muted-foreground">
                                (por: {activity.performed_by_user.full_name || 'Usuário'})
                              </span>
                            )}

                            {/* Badge de agendamento */}
                            {isPending && (
                              <Badge
                                variant="outline"
                                className={cn(
                                  'text-[9px] px-1.5 py-0 h-4 font-medium border',
                                  overdue
                                    ? 'bg-red-100 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-400'
                                    : 'bg-orange-100 text-orange-700 border-orange-200 dark:bg-orange-900/30 dark:text-orange-400',
                                )}
                              >
                                {overdue ? (
                                  <AlertCircle className="h-2.5 w-2.5 mr-1" />
                                ) : (
                                  <CalendarClock className="h-2.5 w-2.5 mr-1" />
                                )}
                                {format(new Date(activity.scheduled_at!), 'dd/MM HH:mm')}
                              </Badge>
                            )}

                            {/* Badge de sistema ou ícone Google Meet para agendamentos online */}
                            {activity.is_system &&
                              (activity.activity_type === 'meeting' && (activity as any).conferencing_link ? (
                                <Tooltip>
                                  <TooltipTrigger>
                                    <img src={googleMeetIcon} alt="Google Meet" className="h-4 w-4" />
                                  </TooltipTrigger>
                                  <TooltipContent>Agendamento Online</TooltipContent>
                                </Tooltip>
                              ) : (
                                <Badge variant="secondary" className="text-[9px] px-1.5 py-0 h-4">
                                  Sistema
                                </Badge>
                              ))}

                            {/* Badge concluída — não aplicável para notas */}
                            {activity.activity_type !== 'note' && activity.completed_at && (
                              <Badge
                                variant="outline"
                                className="text-[9px] px-1.5 py-0 h-4 bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-400"
                              >
                                <CheckCircle2 className="h-2.5 w-2.5 mr-1" />
                                Concluída
                              </Badge>
                            )}
                          </div>

                          <div className="flex items-center gap-1 flex-shrink-0">
                            <span className="text-xs text-muted-foreground whitespace-nowrap">
                              {format(new Date(activity.created_at), 'HH:mm', { locale: ptBR })}
                            </span>

                            {/* Avatar do responsável */}
                            {activity.performed_by_user && (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <div>
                                    <SmartAvatar
                                      src={activity.performed_by_user.avatar_url}
                                      name={activity.performed_by_user.full_name}
                                      size="sm"
                                      className="h-6 w-6 border border-background shadow-sm"
                                    />
                                  </div>
                                </TooltipTrigger>
                                <TooltipContent side="left" className="text-xs">
                                  {activity.performed_by_user.full_name || 'Usuário'}
                                </TooltipContent>
                              </Tooltip>
                            )}

                            {/* Ações (visíveis no hover) - apenas para não-sistema */}
                            {!activity.is_system && (
                              <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                                {/* Marcar como concluída (só se pendente e não-nota) */}
                                {activity.activity_type !== 'note' && !activity.completed_at && (
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-6 w-6 text-muted-foreground hover:text-emerald-600 hover:bg-emerald-100 dark:hover:bg-emerald-900/30"
                                        onClick={() => completeActivity.mutate(activity.id)}
                                        disabled={completeActivity.isPending}
                                      >
                                        <CheckCircle2 className="h-3.5 w-3.5" />
                                      </Button>
                                    </TooltipTrigger>
                                    <TooltipContent side="top" className="text-xs">
                                      Marcar como concluída
                                    </TooltipContent>
                                  </Tooltip>
                                )}

                                {/* Reabrir atividade (só se concluída e não-nota) */}
                                {activity.activity_type !== 'note' && activity.completed_at && (
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-6 w-6 text-muted-foreground hover:text-amber-600 hover:bg-amber-100 dark:hover:bg-amber-900/30"
                                        onClick={() => reopenActivity.mutate(activity.id)}
                                        disabled={reopenActivity.isPending}
                                      >
                                        <RotateCcw className="h-3.5 w-3.5" />
                                      </Button>
                                    </TooltipTrigger>
                                    <TooltipContent side="top" className="text-xs">
                                      Reabrir atividade
                                    </TooltipContent>
                                  </Tooltip>
                                )}

                                {/* v4.0.0 - Editar atividade manual */}
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-6 w-6 text-muted-foreground hover:text-primary hover:bg-primary/10"
                                      onClick={() => setEditingActivity(activity)}
                                    >
                                      <Pencil className="h-3.5 w-3.5" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent side="top" className="text-xs">
                                    Editar
                                  </TooltipContent>
                                </Tooltip>

                                {/* Excluir */}
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-6 w-6 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                                      onClick={() => deleteActivity.mutate(activity.id)}
                                      disabled={deleteActivity.isPending}
                                    >
                                      <Trash2 className="h-3.5 w-3.5" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent side="top" className="text-xs">
                                    Excluir
                                  </TooltipContent>
                                </Tooltip>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Corpo do card */}
                        <div className="px-3 pb-2 space-y-2">
                          {/* Referência ao negócio (v7.0.0: linha discreta, sem caixa) */}
                          {activity.deal && (
                            <div className="flex items-center gap-1.5 min-w-0">
                              <Briefcase className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                              <span className="text-[11px] font-medium text-muted-foreground">
                                {formatDealCode(activity.deal.id)}
                              </span>
                              <span className="text-[11px] text-muted-foreground/50">·</span>
                              <span className="text-[11px] text-muted-foreground/80 truncate">
                                {activity.deal.title}
                              </span>
                            </div>
                          )}

                          {/* v7.5.0: detalhe do evento da OS (linha discreta, sem caixa) */}
                          {serviceOrderDetail && (
                            <div className="flex items-center gap-1.5 min-w-0">
                              <Wrench className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                              <span className="text-[11px] text-muted-foreground/80 truncate">
                                {serviceOrderDetail}
                              </span>
                            </div>
                          )}

                          {/* v7.4.0: linha secundária da perda — bolinha colorida do motivo
                              + nome e, com divisor "|", a observação (tooltip com texto cheio) */}
                          {hasLossDetail && (
                            <div className="flex items-center gap-1.5 text-xs min-w-0">
                              <span
                                className="h-2 w-2 rounded-full flex-shrink-0 ring-1 ring-black/10"
                                style={{ backgroundColor: lossReasonColor || '#EF4444' }}
                              />
                              {lossReasonName && (
                                <span className="font-medium text-foreground truncate">
                                  {lossReasonName}
                                </span>
                              )}
                              {lossReasonNote && (
                                <>
                                  {lossReasonName && (
                                    <span className="text-muted-foreground/60 select-none">|</span>
                                  )}
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <span className="text-muted-foreground truncate">
                                        {lossReasonNote}
                                      </span>
                                    </TooltipTrigger>
                                    <TooltipContent className="max-w-xs text-xs">
                                      {lossReasonNote}
                                    </TooltipContent>
                                  </Tooltip>
                                </>
                              )}
                            </div>
                          )}


                          {/* Descrição com expandir/colapsar */}
                          {activity.description && (
                            <div>
                              <div
                                className={cn(
                                  'bg-muted/60 rounded-md px-3 py-2 text-xs text-foreground [&_*]:text-foreground cursor-pointer',
                                  !expandedDescriptions.has(activity.id) && 'line-clamp-2',
                                )}
                                onClick={() => toggleDescription(activity.id)}
                                dangerouslySetInnerHTML={{ __html: activity.description }}
                              />
                              <button
                                type="button"
                                className="text-[10px] text-primary/70 hover:text-primary mt-0.5"
                                onClick={() => toggleDescription(activity.id)}
                              >
                                {expandedDescriptions.has(activity.id) ? 'Ver menos' : 'Ver mais'}
                              </button>
                            </div>
                          )}

                          {/* v7.2.0: detalhes dos eventos de PROPOSTA (valor e número),
                              no mesmo formato da timeline do modal de negócios */}
                          {isProposal && (
                            <div className="flex flex-wrap gap-1.5">
                              {proposalNv?.total != null && (
                                <Badge variant="outline" className="text-[10px] gap-1 px-1.5 py-0.5">
                                  <DollarSign className="h-3 w-3" />
                                  R$ {Number(proposalNv.total || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                                </Badge>
                              )}
                              {proposalNv?.proposal_number && (
                                <span
                                  className="inline-flex items-center gap-1 text-[10px] text-sky-700 dark:text-sky-300"
                                  title={proposalNv?.proposal_title || 'Proposta comercial'}
                                >
                                  <ScrollText className="h-3 w-3" />
                                  Proposta
                                  {` #${proposalNv.proposal_number}`}
                                  {proposalNv?.version_number ? ` v${proposalNv.version_number}` : ''}
                                </span>
                              )}
                            </div>
                          )}

                          {/* v7.3.0: cobranças geradas — card unificado (avulsa ou agrupada
                              por assinatura). Agregados vêm prontos do backend. */}
                          {activity.activity_type === 'charge_generated' && (
                            <ChargeActivityCard
                              newValue={(activity as any).new_value as Record<string, any> | null}
                            />
                          )}


                          {/* Anexos da atividade */}
                          {activity.attachments && activity.attachments.length > 0 && (
                            <ActivityAttachmentsDisplay
                              attachments={
                                activity.attachments as unknown as Parameters<
                                  typeof ActivityAttachmentsDisplay
                                >[0]['attachments']
                              }
                              compact={true}
                            />
                          )}

                          {/* Comentários - oculto para atividades do sistema ou modo compacto */}
                          {!activity.is_system && !compact && (
                            <div className="pt-2 border-t border-border/50">
                              <ActivityCommentSection
                                activityId={activity.id}
                                commentsCount={activity.comments_count || 0}
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );

                })}
              </div>
            </div>
          ))}

          {/* Indicador de mais itens (modo legado) */}
          {hasMore && (
            <div className="text-center pt-3 pl-8">
              <span className="text-xs text-muted-foreground">
                +{rawActivities.length - (maxItems || 0)} atividades anteriores
              </span>
            </div>
          )}

          {/* Sentinela do IntersectionObserver para scroll infinito (modo paginado) */}
          {paginated && <div ref={sentinelRef} className="h-1" />}

          {/* Spinner de carregamento da próxima página */}
          {paginated && paginatedResult.isFetchingNextPage && (
            <div className="flex items-center justify-center py-3">
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              <span className="ml-2 text-xs text-muted-foreground">
                Carregando mais atividades...
              </span>
            </div>
          )}
        </div>
      </div>

      {/* v4.0.0 - Sheet de edição de atividade manual (paridade com Deal Timeline) */}
      <ActivityEditSheet
        open={!!editingActivity}
        onOpenChange={(open) => !open && setEditingActivity(null)}
        activity={editingActivity as any}
        dealId={editingActivity?.deal?.id ?? null}
        onSuccess={() => setEditingActivity(null)}
      />
    </TooltipProvider>
  );
}
